import { RouterState, RouterStore } from "mobx-state-router";
import { BatchList } from "../../models/batch/BatchList";
import BatchFormState from "../../components/codes/batches/BatchFormState";
import BatchListState from "../../components/codes/batches/BatchListState";
import { buildColumns as buildBatchColumns } from "../../pages/codes/batches/List";
import { faMailBulk } from "@fortawesome/free-solid-svg-icons";
import async from "../../components/Async";
import React from "react";
import {BatchFilterSelectState} from "../../components/elements/filters/BatchFilterSelectState";
import BatchItemListState from "src/components/codes/batches/items/BatchItemListState";
import {BatchItemList} from "../../models/batch/BatchItemList";
import { buildColumns as buildBatchItemsColumns } from "../../pages/codes/batches/items/BatchItemsList";
import { CodeModelList } from "../../models/codes/Code";
import { QrDownloadState } from "../../components/qr/qrpreview/qrDownload/QrDownloadState";
import {checkUserPermissions} from "../../utils/requiredRolesCheck";
import {getSnapshot, clone, detach, resolveIdentifier, getRoot, getEnv} from "mobx-state-tree";
import {TagModel} from "../../models/tags/TagModel";
import {batchTypesConstatns, batchEditTypes, batchEditAdvancedConfigs} from "../../utils/constants";
import {UtmModalState} from "../../components/utms/modal/UtmModalState";
import {Utm} from "../../models/utms/UtmModel";
import {capitalizeFirstLetter} from "../../utils/common";
import { ModalState } from "../../components/elements/modal/GenericModal";
import Parameters from "../../models/Parameters";
import {initParameterSetModal, initParameterSelectOptions} from "../../components/configs/parametersets/utils/ParameterSetsUtil";
import {initRuleSetModalState} from "../../components/configs/rulesets/utils/RuleSetsUtil";
import QrPreviewState from "../../components/qr/qrpreview/QrPreviewState";
import {initDesignerTemplateModal, isMediaFileEnabled} from "../../components/qr/designer/utils/DesignerTemplateUtil";
import {initUtmParameterSelectState} from "../../components/utms/utils/UtmUtils";
import {StatusState} from "../../components/configs/status/StatusState";
import {DefaultSettingModel} from "../../models/defaultSetting/DefaultSettingModel";
import {initExtendedDataModal} from "../../components/extendedData/utils/ExtendedDataUtil";

const BatchesPage = async(() => import("../../pages/codes/batches/List"));
const BatchItemsPage = async(() => import("../../pages/codes/batches/items/BatchItemsList"));

export const viewMap = {
  batches: <BatchesPage/>,
  batchItems: <BatchItemsPage/>
}

export const routeDefinitions = [
  {
    path: "batches",
    name: "Batches",
    icon: faMailBulk,
    component: BatchesPage,
    children: null,
    featureFlagRequired: "cmp_ui_batches"
  }
]

const isFeatureToggleEnabled = (value) => value === true || value === 'true'

const granularFeatureToggleKeys = {
  create: 'batch_advanced_configs_create',
  edit: 'batch_advanced_configs_edit',
}

// batchFormContext: 'create' | 'edit'. Tenants can set batch_advanced_configs_create /
// batch_advanced_configs_edit for granular control; if a specific key isn't set for the
// tenant (or batchFormContext isn't 'create'/'edit'), we fall back to the legacy single
// batch_advanced_configs flag (enables both).
export const getBatchAdvancedConfigs = (appConfigs, batchFormContext) => {
  const featureToggles = appConfigs?.feature_toggles || {}
  const specificKey = granularFeatureToggleKeys[batchFormContext]

  if (specificKey && Object.prototype.hasOwnProperty.call(featureToggles, specificKey)) {
    return isFeatureToggleEnabled(featureToggles[specificKey])
  }

  return isFeatureToggleEnabled(featureToggles.batch_advanced_configs)
}

// Guards against hydrating an existing batch's editType as "Others" (the gateway to
// UTM/Parameters/Rules/Status/Designer QR) when the tenant's advanced-config flag is
// currently disabled - e.g. a batch saved as "Others" before the flag was turned off.
export const resolveEditType = (editType, batchAdvancedConfigsEnabled) => {
  if (editType === batchEditTypes.OTHERS && !batchAdvancedConfigsEnabled) {
    return batchEditTypes.TAG
  }
  return editType
}

export const routeTransitions = [
  {
    name: 'batches',
    pattern: '/batches',
    beforeEnter: checkUserPermissions,
    onEnter: async (
      fromState,
      toState,
      routerStore,
    ) => {
      const BaseConfig = {
        "dataShape": 0, "imageFormat": "png",
        "colorMode": "rgb", "quietZone": 4,
        "errorCorrection": "M", "size": 1,
        "resolution": 1200,
      }
      const {rootStore} = routerStore.options;
      const groupSelectState = rootStore.rootUiStore.groupSelectStore;
      const tagSelectStore = rootStore.rootUiStore.tagSelectStore;
      const batchFilterSelectState = rootStore.rootUiStore.batchFilterSelectState
      const dimensionSelectViewState = rootStore.rootUiStore.dimensionSelectViewState
      const dimensionsMultiSelectStore = rootStore.rootUiStore.dimensionsMultiSelectState
      const publishDomainSelectStore = rootStore.rootUiStore.publishDomainSelectStore
      const codeFilterSelectState = rootStore.rootUiStore.codeFilterSelectState;
      const qrConfigSelectStore = rootStore.rootUiStore.qrConfigSelectStore
      const batchSelectStore = rootStore.rootUiStore.batchSelectStore;
      const utmSelectState = rootStore.rootUiStore.utmSelectState;
      const batchList = rootStore.rootUiStore.batchList;
      const mediaFileSelectState = rootStore.rootUiStore.mediaFileSelectState
      const defaultSettingFeatureFlag = rootStore?.appConfigs?.features?.cmp_ui_default_settings?.enabled

      // Initialize extended data stores
      const extendedDataStore = rootStore.domainStore.extendedDataStore;
      await extendedDataStore.init();

      batchList.clearSelected();
      const tenantStore = rootStore.domainStore.tenantStore;
      groupSelectState?.selectDefaultGroup(tenantStore.defaultGroupUuid);

      codeFilterSelectState.reset();
      tagSelectStore?.tagStore?.init(); //reset tags
      tagSelectStore?.setSelectedTags([]);
      dimensionSelectViewState?.clearSelections()
      dimensionSelectViewState?.setDefaults()
      batchFilterSelectState.reset()
      qrConfigSelectStore.setSelectedGroup(groupSelectState?.selectedGroup?.uuid)
      publishDomainSelectStore.refresh(groupSelectState?.selectedGroup);
      const codes = CodeModelList.create({});
      // batchList.setSelectedBatch("68f1e172-b1de-40bf-b082-e53f24c4acac");
      // const snapshot = getSnapshot(tagSelectStore);
      // const newSelectedTags = {
      //   ...snapshot,
      //   selectStateUuid: 'ntss'
      // }
      let newSelectedTags = []
      tagSelectStore.tagStore.tags.forEach((tag) => {
        const tagModel = resolveIdentifier(TagModel, getRoot(tagSelectStore), tag.uuid);
        if (tagModel) {
          newSelectedTags.push(tag.uuid);
        }
      });

      // init parameter select options
      await initParameterSelectOptions(rootStore)

      const createAdvancedConfigsEnabled = getBatchAdvancedConfigs(rootStore.appConfigs, 'create')
      const editAdvancedConfigsEnabled = getBatchAdvancedConfigs(rootStore.appConfigs, 'edit')
      // create new modal state for utm
      const modalState = ModalState.create({});
      rootStore.rootUiStore.utmSelectState?.store.init({})
      const utmParameterSelectStore = await initUtmParameterSelectState(rootStore)
      const utmModalState = UtmModalState.create({
        utmSelectState:utmSelectState,
        groupSelectStore:groupSelectState,
        utmParameterSelectState: utmParameterSelectStore,
        utm:Utm.create({uuid:"modalUtm"})
      })

      // parameterset modal
      const parameterSetModalState = await initParameterSetModal(rootStore, groupSelectState, false)

      // rule set modal
      const ruleSetModalState = await initRuleSetModalState(rootStore, "batchRuleSet", false)

      // status modal
      const statusViewState = StatusState.create(({
        title:"Status"}
      ))
      const batchStatus = StatusState.create(({
        title:"BatchStatus"}
      ))

      // designer template modal
      const mediaFileEnabled = isMediaFileEnabled(rootStore.appConfigs);
      const designerModalState = initDesignerTemplateModal(getSnapshot(rootStore.domainStore.dashboardStore), qrConfigSelectStore, groupSelectState, false, dimensionsMultiSelectStore, tagSelectStore, rootStore.rootUiStore.mediaFileSelectState, mediaFileEnabled);

      // preview modal
      const qrPreviewStore = QrPreviewState.create({
        isEditCodePage: false,
        qrConfigSelectStore: qrConfigSelectStore,
        baseQRImageFormData: JSON.stringify(BaseConfig),
        modalStore: ModalState.create({}),
        qrDownloadStore: QrDownloadState.create({}),
        // dashboardStore: getSnapshot(rootStore.domainStore.dashboardStore),
        defaultImageConfig: getSnapshot(rootStore.domainStore.dashboardStore).apps.defaultImageConfig,
        v1ImageConfig: getSnapshot(rootStore.domainStore.dashboardStore).apps.v1ImageConfig
      })

      // Initialize extended data modal using utility
      const extendedDataSettingModalState = await initExtendedDataModal(rootStore, false);

      // const initBatchStore = BatchStore.create({storeUuid: 'btts'});
      // initBatchStore.getBatches();
      // const batchSelectStore = BatchSelectState.create({selectStateUuid: 'bss',})
      // batchSelectStore.setBatches(initBatchStore);

      //batchSelectStore.batchStore.getBatches();
      // batchList.clearSelected();
      // console.log('batchList:', batchList)
      const protocolDefaultsEnabled = rootStore.appConfigs?.configs?.protocol_defaults_enabled ?? false;
      const protocolDefaults = rootStore.appConfigs?.configs?.protocol_defaults ?? {};
      const batchFormState = BatchFormState.create({
        title: "New Batch",
        tagSelectStore: tagSelectStore,
        groupSelectStore: groupSelectState,
        dimensionSelectViewState: dimensionSelectViewState,
        publishDomainSelectStore: publishDomainSelectStore,
        codeFilterSelectState: codeFilterSelectState,
        protocolDefaultsEnabled: protocolDefaultsEnabled,
        protocolDefaults: protocolDefaults,
        qrConfigSelectStore: qrConfigSelectStore,
        codeModels: codes,
        batchSelectStore: batchList,
        qrDownloadStore: QrDownloadState.create({}),
        v1ImageConfig: getSnapshot(rootStore.domainStore.dashboardStore).apps.v1ImageConfig,
        availableNewSelectedTags: newSelectedTags,
        batchType: batchTypesConstatns.CREATE,
        modalStore: modalState,
        utmModalState: utmModalState,
        utm:Utm.create({uuid:"appliedUtm"}),
        parameters: Parameters.create({}),
        parameterSetModalState: parameterSetModalState,
        ruleSetModalState: ruleSetModalState,
        designerModalState: designerModalState,
        statusViewState: statusViewState,
        batchStatus: batchStatus,
        createAdvancedConfigsEnabled: createAdvancedConfigsEnabled,
        editAdvancedConfigsEnabled: editAdvancedConfigsEnabled,
        defaultSetting: DefaultSettingModel.create({uuid:"Create Default Setting"}),
        defaultSettingFeatureFlag: defaultSettingFeatureFlag,
        extendedDataSettingModalState: extendedDataSettingModalState
      })

      // Attach BatchFormState to rootStore tree to enable MST reference resolution
      await rootStore.rootUiStore.attachPageState('batchFormState', batchFormState)

      const viewState = BatchListState.create({
        store: batchList,
        columns: buildBatchColumns(routerStore, batchList),
        groupSelectStore: groupSelectState,
        batchFormState: batchFormState,
        batchFilterSelectState: batchFilterSelectState
      });

      console.log("Attaching Page State : batchListViewState ", viewState);
      await rootStore.rootUiStore.attachPageState('batchListViewState', viewState)
      await rootStore.rootUiStore.attachPageState('qrPreviewStore', qrPreviewStore)
      await batchFormState.parameterSetModalState.fetchAllParameters()
      // load codes filter list
      await batchFormState.fetchFilters();
      // Now BatchFormState is in MST tree - it can access its references directly
      await batchFormState.fetchAndHydrateDefaultSetting();
    },
    onExit: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      //setTimeout(()=>{rootStore.rootUiStore.detachPageState('batchListViewState')},100)
      const batchList = rootStore.rootUiStore.batchList;
      batchList.clearSelected();
    }
  },
  {
    name: 'batchItems',
    pattern: '/batches/:batchUuid/items',
    beforeEnter: checkUserPermissions,
    onEnter: async (
      fromState,
      toState,
      routerStore,
    ) => {
      const {rootStore} = routerStore.options;
      const groupSelectState = rootStore.rootUiStore.groupSelectStore;
      const tagSelectStore = rootStore.rootUiStore.tagSelectStore;
      const batchFilterSelectState = rootStore.rootUiStore.batchFilterSelectState
      const dimensionSelectViewState = rootStore.rootUiStore.dimensionSelectViewState
      const dimensionsMultiSelectStore = rootStore.rootUiStore.dimensionsMultiSelectState
      const publishDomainSelectStore = rootStore.rootUiStore.publishDomainSelectStore
      const codeFilterSelectState = rootStore.rootUiStore.codeFilterSelectState;
      const qrConfigSelectStore = rootStore.rootUiStore.qrConfigSelectStore
      const qrConfigStore = rootStore.domainStore.qrConfigStore
      const utmSelectState = rootStore.rootUiStore.utmSelectState;
      const mediaFileSelectState = rootStore.rootUiStore.mediaFileSelectState

      // Initialize extended data stores
      const extendedDataStore = rootStore.domainStore.extendedDataStore;
      await extendedDataStore.init();

      const batchList = rootStore.rootUiStore.batchList;
      batchList.clearSelected();
      codeFilterSelectState.reset();
      codeFilterSelectState.setIsDisabled(true);
      tagSelectStore?.tagStore?.init(); //reset tags
      tagSelectStore?.setSelectedTags([]);
      dimensionSelectViewState?.clearSelections()
      dimensionSelectViewState?.setDefaults()
      batchFilterSelectState.reset()

      //qrConfigSelectStore.setSelectedGroup(groupSelectState.groupStore?.groups[0].uuid)

      publishDomainSelectStore.refresh(groupSelectState.groupStore?.groups[0]);
      const codes = CodeModelList.create({});

      const selectedBatch = await getEnv(rootStore).batchManager.fetchBatchDetail(toState.params.batchUuid)

      // Only set config here if imageUuid is not present (to avoid loading same config twice later at line ~598)
      // We check for images.configs to determine if we'll be loading the config again later
      if (!selectedBatch.details.images?.configs) {
        qrConfigSelectStore.setSelectedConfig(selectedBatch.details.qrConfig)
      }

      let batchType = "Create"
      if(selectedBatch.batchType === "CREATE"){
        batchType = batchTypesConstatns.CREATE
      } else if(selectedBatch.batchType === "DOWNLOAD"){
        batchType = batchTypesConstatns.DOWNLOAD
      } else if(selectedBatch.batchType === "EDIT"){
        batchType = batchTypesConstatns.EDIT
      }

      const batchItems = BatchItemList.create({
        batchUuid: toState.params.batchUuid
      })

      let selectedTags = []
      tagSelectStore.tagStore.tags.forEach((tag) => {
        const tagModel = resolveIdentifier(TagModel, getRoot(tagSelectStore), tag.uuid);
        if (tagModel) {
          selectedTags.push(tag.uuid);
        }
      });

      let selectedCodeType = "url"
      if(selectedBatch.details.codeType){
        selectedCodeType = selectedBatch.details.codeType
      }

      const createAdvancedConfigsEnabled = getBatchAdvancedConfigs(rootStore.appConfigs, 'create')
      const editAdvancedConfigsEnabled = getBatchAdvancedConfigs(rootStore.appConfigs, 'edit')
      // This batch's own batchType (CREATE/DOWNLOAD/EDIT) decides which flag applies here -
      // not "this is the batch-detail page", since that page shows batches of any type.
      const batchAdvancedConfigsEnabled = batchType === batchTypesConstatns.EDIT ? editAdvancedConfigsEnabled : createAdvancedConfigsEnabled

      // generate selected advanced configs
      const selectedAdvancedConfigs = selectedBatch.details.editDetails ? selectedBatch.details.editDetails.options : [batchEditAdvancedConfigs.UTM, batchEditAdvancedConfigs.PARAMETERS, batchEditAdvancedConfigs.RULES, batchEditAdvancedConfigs.STATUS, batchEditAdvancedConfigs.DESIGNER_QR, batchEditAdvancedConfigs.EXTENDED_DATA]

      // init parameter select options
      await initParameterSelectOptions(rootStore)

      // create new modal state for utm
      const modalState = ModalState.create({});
      rootStore.rootUiStore.utmSelectState?.store.init({})
      let utmCampaign = ""
      let utmSource = ""
      let utmMedium = ""
      let utmTerm = ""
      let utmContent = ""
      let campaignSelectValue = null
      let sourceSelectValue = null
      let mediumSelectValue = null
      let termSelectValue = null
      let contentSelectValue = null
      let batchEditCampaignNochanges= false
      let batchEditSourceNochanges= false
      let batchEditMediumNochanges= false
      let batchEditTermNochanges= false
      let batchEditContentNochanges= false
      let isRemovedUTM = false
      if(selectedBatch.details.utm){
        if(selectedBatch.details.utm.campaign){
          utmCampaign = selectedBatch.details.utm.campaign
        }
        if(selectedBatch.details.utm.source){
          utmSource = selectedBatch.details.utm.source
        }
        if(selectedBatch.details.utm.medium){
          utmMedium = selectedBatch.details.utm.medium
        }
        if(selectedBatch.details.utm.term){
          utmTerm = selectedBatch.details.utm.term
        }
        if(selectedBatch.details.utm.content){
          utmContent = selectedBatch.details.utm.content
        }
        if(selectedBatch.details.utm.utmParametersConfigs && selectedBatch.details.utm.utmParametersConfigs.campaignSelectValue){
          campaignSelectValue = selectedBatch.details.utm.utmParametersConfigs.campaignSelectValue
        }
        if(selectedBatch.details.utm.utmParametersConfigs && selectedBatch.details.utm.utmParametersConfigs.sourceSelectValue){
          sourceSelectValue = selectedBatch.details.utm.utmParametersConfigs.sourceSelectValue
        }
        if(selectedBatch.details.utm.utmParametersConfigs && selectedBatch.details.utm.utmParametersConfigs.mediumSelectValue){
          mediumSelectValue = selectedBatch.details.utm.utmParametersConfigs.mediumSelectValue
        }
        if(selectedBatch.details.utm.utmParametersConfigs && selectedBatch.details.utm.utmParametersConfigs.termSelectValue){
          termSelectValue = selectedBatch.details.utm.utmParametersConfigs.termSelectValue
        }
        if(selectedBatch.details.utm.utmParametersConfigs && selectedBatch.details.utm.utmParametersConfigs.contentSelectValue){
          contentSelectValue = selectedBatch.details.utm.utmParametersConfigs.contentSelectValue
        }
        if(selectedBatch.details.utm.batchEditCampaignNochanges) {
          batchEditCampaignNochanges = selectedBatch.details.utm.batchEditCampaignNochanges
        }
        if(selectedBatch.details.utm.batchEditSourceNochanges) {
          batchEditSourceNochanges = selectedBatch.details.utm.batchEditSourceNochanges
        }
        if(selectedBatch.details.utm.batchEditMediumNochanges) {
          batchEditMediumNochanges = selectedBatch.details.utm.batchEditMediumNochanges
        }
        if(selectedBatch.details.utm.batchEditTermNochanges) {
          batchEditTermNochanges = selectedBatch.details.utm.batchEditTermNochanges
        }
        if(selectedBatch.details.utm.batchEditContentNochanges) {
          batchEditContentNochanges = selectedBatch.details.utm.batchEditContentNochanges
        }
        if(selectedBatch.details.utm.removedCampaign && selectedBatch.details.utm.removedSource && selectedBatch.details.utm.removedMedium && selectedBatch.details.utm.removedTerm && selectedBatch.details.utm.removedContent){
          // check user removed any utm parameters
          isRemovedUTM = true
        }
      }
      let utm = Utm.create({
        uuid:"modalUtm",
        campaign: utmCampaign,
        source: utmSource,
        medium: utmMedium,
        term: utmTerm,
        content: utmContent,
        utmParametersConfigs: {
          campaignSelectValue: campaignSelectValue,
          sourceSelectValue: sourceSelectValue,
          mediumSelectValue: mediumSelectValue,
          termSelectValue: termSelectValue,
          contentSelectValue: contentSelectValue
        },
        batchEditCampaignNochanges: batchEditCampaignNochanges,
        batchEditSourceNochanges: batchEditSourceNochanges,
        batchEditMediumNochanges: batchEditMediumNochanges,
        batchEditTermNochanges: batchEditTermNochanges,
        batchEditContentNochanges: batchEditContentNochanges
      })
      // has to create a copy of utm for mobx. should refactor utm
      let utmCopy = Utm.create({
        uuid:"copyUtm",
        campaign: utmCampaign,
        source: utmSource,
        medium: utmMedium,
        term: utmTerm,
        content: utmContent,
        utmParametersConfigs: {
          campaignSelectValue: campaignSelectValue,
          sourceSelectValue: sourceSelectValue,
          mediumSelectValue: mediumSelectValue,
          termSelectValue: termSelectValue,
          contentSelectValue: contentSelectValue
        },
        batchEditCampaignNochanges: batchEditCampaignNochanges,
        batchEditSourceNochanges: batchEditSourceNochanges,
        batchEditMediumNochanges: batchEditMediumNochanges,
        batchEditTermNochanges: batchEditTermNochanges,
        batchEditContentNochanges: batchEditContentNochanges
      })
      const utmParameterSelectStore = await initUtmParameterSelectState(rootStore)
      const utmModalState = UtmModalState.create({
        utmSelectState:utmSelectState,
        groupSelectStore:groupSelectState,
        utmParameterSelectState: utmParameterSelectStore,
        utm: utm,
        isView: true,
        isRemovedUTM: isRemovedUTM
      })

      // parameterset modal
      let isRemovedParameterSet = false
      if(selectedBatch.details.parameters && selectedBatch.batchType !== batchTypesConstatns.CREATE.toUpperCase()){
        if(selectedBatch.details.parameters.codeParamType.length == 0 && selectedBatch.details.parameters.scanParamType.length == 0 &&
           selectedBatch.details.parameters.countParamType.length == 0 && selectedBatch.details.parameters.premiumParamType.length == 0 && selectedBatch.details.parameters.customParamType.length == 0
          && selectedBatch.details.parameters.gs1ParamType.length == 0 && selectedBatch.details.parameters.edParamType.length == 0){
          isRemovedParameterSet = true
        }
      }
      const parameterSetModalState = await initParameterSetModal(rootStore, groupSelectState, true, isRemovedParameterSet)
      let parameters = Parameters.create({
        batchCodeParamTypeLock: selectedBatch.details.parameters?.batchCodeParamTypeLock || false,
        batchScanParamTypeLock: selectedBatch.details.parameters?.batchScanParamTypeLock || false,
        batchCountParamTypeLock: selectedBatch.details.parameters?.batchCountParamTypeLock || false,
        batchPremiumParamTypeLock: selectedBatch.details.parameters?.batchPremiumParamTypeLock || false,
        batchCustomParamTypeLock: selectedBatch.details.parameters?.batchCustomParamTypeLock || false,
        batchGS1ParamTypeLock: selectedBatch.details.parameters?.batchGS1ParamTypeLock || false,
        batchEDParamTypeLock: selectedBatch.details.parameters?.batchEDParamTypeLock || false,
      })
      if(selectedBatch.details.parameters){
        parameters.hydrateParameters(selectedBatch.details.parameters)
      }

      // rule set modal
      const ruleSetModalState = await initRuleSetModalState(rootStore, "batchDetailRuleSet", true)

      // status modal
      let scheduleStatusChange = selectedBatch.details.statusDetail?.schedule ? true : false
      let statusScheduleData = selectedBatch.details.statusDetail ? JSON.stringify(selectedBatch.details.statusDetail.schedule) : '{"status" : [{"date" : ""}]}'
      const statusViewState = StatusState.create(({
        title: "Status",
        status: selectedBatch.details.codeStatus,
        scheduleStatusChange: scheduleStatusChange,
        statusScheduleData: statusScheduleData,
        isView: true,
        isBatch: true,
        isChangedStatus: scheduleStatusChange //|| (selectedBatch.details.codeStatus ? true : false)
      }))
      let batchStatus = StatusState.create(({title:"BatchStatus"}))

      // designer template modal
      const mediaFileEnabled = isMediaFileEnabled(rootStore.appConfigs);
      const designerModalState = initDesignerTemplateModal(getSnapshot(rootStore.domainStore.dashboardStore), qrConfigSelectStore, groupSelectState, true, dimensionsMultiSelectStore, tagSelectStore, rootStore.rootUiStore.mediaFileSelectState, mediaFileEnabled)

      // Initialize extended data modal using utility
      const extendedDataSettingModalState = await initExtendedDataModal(rootStore, true);

      // preview modal
      const qrPreviewStore = QrPreviewState.create({
        isEditCodePage: false,
        qrConfigSelectStore: qrConfigSelectStore,
        baseQRImageFormData: JSON.stringify(selectedBatch.details.qrConfig),
        modalStore: ModalState.create({}),
        qrDownloadStore: QrDownloadState.create({}),
        dashboardStore: getSnapshot(rootStore.domainStore.dashboardStore),
        defaultImageConfig: getSnapshot(rootStore.domainStore.dashboardStore).apps.defaultImageConfig,
        v1ImageConfig: getSnapshot(rootStore.domainStore.dashboardStore).apps.v1ImageConfig
      })
      let imageUuid = ""
      if(selectedBatch.details.images){
         imageUuid = selectedBatch.details.images.configs
      }

      // if batch is download batch, then need to show image format
      let imageFormat = ""
      const qrDownloadStore = QrDownloadState.create({})
      if(selectedBatch.batchType == batchTypesConstatns.DOWNLOAD.toUpperCase()) {
        imageFormat = selectedBatch.details.imageFormat
        qrDownloadStore.setImageFormat(imageFormat)
      }

      // if batch is edit batch, then need to set editing value
      let editType = ""
      let editValue = ""
      let tagOperation = ""
      let newSelectedTags = []
      if(selectedBatch.batchType == batchTypesConstatns.EDIT.toUpperCase()) {
        const storedEditType = capitalizeFirstLetter(selectedBatch.details.editType.toLowerCase())

        // Parse editDetails based on what's ACTUALLY stored, not the (possibly downgraded)
        // display value below - editDetails' shape depends on the real stored editType.
        if(storedEditType == batchEditTypes.TAG){
          tagOperation = capitalizeFirstLetter(selectedBatch.details.editDetails.tagOperation.toLowerCase())
          tagSelectStore.tagStore.tags.forEach((tag) => {
            if(storedEditType === batchEditTypes.TAG && selectedBatch.details.editDetails.tags.includes(tag.uuid)){
              const tagModel = resolveIdentifier(TagModel, getRoot(tagSelectStore), tag.uuid);
              if (tagModel) {
                newSelectedTags.push(tag.uuid);
              }
            }
          });
        } else if(storedEditType == batchEditTypes.STATUS){
          editValue = selectedBatch.details.editDetails.status
        } else if(storedEditType == batchEditTypes.URL){
          editValue = selectedBatch.details.editDetails.landingUrl
        }

        editType = resolveEditType(storedEditType, batchAdvancedConfigsEnabled)
      }

      // check batch is default batch operation or not
      let isDefaultBatchOperation = false
      if(selectedBatch.details.defaultOperation.hasOwnProperty('batchUuid')){
        isDefaultBatchOperation = true
        batchList.setSelectedBatch(selectedBatch.details.defaultOperation.batchUuid)
      }
      // set created date
      const createdAt = new Date(selectedBatch.createdAt);
      const defaultSettingFeatureFlag = rootStore?.appConfigs?.features?.cmp_ui_default_settings?.enabled
      const batchFormState = BatchFormState.create({
        title: selectedBatch.fileName,
        tagSelectStore: tagSelectStore,
        groupSelectStore: groupSelectState,
        dimensionSelectViewState: dimensionSelectViewState,
        publishDomainSelectStore: publishDomainSelectStore,
        codeFilterSelectState: codeFilterSelectState,
        qrConfigSelectStore: qrConfigSelectStore,
        //selectedQrConfig: selectedBatch.details.qrConfig,
        codeModels: codes,
        batchSelectStore: batchList,
        qrDownloadStore: qrDownloadStore,
        v1ImageConfig: getSnapshot(rootStore.domainStore.dashboardStore).apps.v1ImageConfig,
        codeStatus: selectedBatch.details.codeStatus,
        selectedCodeType: selectedCodeType,
        batchName: selectedBatch.fileName,
        batchType: batchType,
        imageFormat: imageFormat,
        editType: editType,
        editValue: editValue,
        tagOperation: tagOperation,
        availableNewSelectedTags: selectedTags,
        updatedSelectedTags: newSelectedTags,
        isBatchEditable: false,
        isDefaultBatchOperation: isDefaultBatchOperation,
        createdDate: createdAt.toLocaleString(),
        batchUuid: toState.params.batchUuid,
        modalStore: modalState,
        utmModalState: utmModalState,
        utm: utmCopy,
        parameters: parameters,
        parameterSetModalState: parameterSetModalState,
        ruleSetModalState: ruleSetModalState,
        rules: JSON.stringify(selectedBatch.details.rules),
        designerModalState: designerModalState,
        imageUuid: imageUuid,
        statusViewState: statusViewState,
        batchStatus: batchStatus,
        selectedAdvancedConfigs: selectedAdvancedConfigs,
        createAdvancedConfigsEnabled: createAdvancedConfigsEnabled,
        editAdvancedConfigsEnabled: editAdvancedConfigsEnabled,
        defaultSettingFeatureFlag: defaultSettingFeatureFlag,
        extendedDataSettingModalState: extendedDataSettingModalState
      })
      await rootStore.rootUiStore.attachPageState('batchFormState', batchFormState)
      if(selectedBatch.details.extendedData){
        extendedDataSettingModalState.hydrateModal(selectedBatch.details)
      }
      //batchFormState.selectQrConfig(imageConfig)
      // batchFormState.setOnSelectChanges()
      // console.log('batchDetails', batchDetails.owner)
      const batchItemListState = BatchItemListState.create({
        store: batchItems,
        columns: buildBatchItemsColumns(routerStore),
        //batch: selectedBatch,
        batchFormState: batchFormState,
      })
      console.log("Attaching Page State : batchItemListViewState ", batchItemListState)
      await rootStore.rootUiStore.attachPageState('batchItemListViewState', batchItemListState)
      await rootStore.rootUiStore.attachPageState('qrPreviewStore', qrPreviewStore)
      // parameter modal setting
      await batchFormState.parameterSetModalState.fetchAllParameters()
      batchFormState.parameterSetModalState.hydrateForm(batchFormState)


      if(imageUuid){
        const imageResponse = await rootStore.domainStore.qrConfigStore?.fetchImageConfigWithId(imageUuid)
        qrPreviewStore?.setQRImageFormData(imageResponse.qrConfig)
        qrConfigSelectStore.setSelectedConfig(imageResponse)
      }
    },
    onExit: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      const codeFilterSelectState = rootStore.rootUiStore.codeFilterSelectState;
      const batchList = rootStore.rootUiStore.batchList;
      codeFilterSelectState.reset();
      batchList.clearSelected();
    }
  }
];
